import { useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import useStore from "../context/useStore";
import { repurchaseService } from "../services/repurchaseService";
import carburantTools from "../helpers/carburantTools";

const DEBOUNCE_MS = 400;

// Convertit le taux brut (fraction) renvoyé par l'API en pourcentage, sans
// arrondi intermédiaire : CalculerVRDepuisTableau (API, financement) utilise
// le taux interpolé brut, le front doit conserver la même précision (LS-665).
export const tauxRawToPourcentage = (tauxResolu) => {
  const brut =
    tauxResolu !== null && tauxResolu !== undefined && tauxResolu !== ""
      ? Number(tauxResolu)
      : NaN;
  return Number.isFinite(brut) ? brut * 100 : null;
};

// Résout la grille VR et le taux applicables au devis via l'API
// (GET repurchases/simuler-grille), à la date du jour.
// Retourne le taux en POURCENTAGE (l'API renvoie une fraction).
const useSimulatedRateVR = (vehicle, dur, km) => {
  const [state] = useStore();
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const callIdRef = useRef(0);

  const fuelTypes = state.constant.items?.FUEL_TYPES || [];
  const carburants = state.constant.items?.FLOTTE?.CARBURANTS || [];

  const marque = vehicle?.brand?.name;
  const modele = vehicle?.brandModel?.name;
  const carburant = carburantTools.fuelTypeIdToCarburantId(
    vehicle?.brandModelFinish?.fuelType?.id,
    fuelTypes,
    carburants
  );

  useEffect(() => {
    if (!marque || !modele || !carburant || !dur || !km) return;

    const callId = ++callIdRef.current;
    const timer = setTimeout(async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await repurchaseService.simulateGrille({
          date: DateTime.now().toISODate(),
          marque,
          modele,
          carburant,
          duree: dur,
          kilometrage: km,
        });
        // Réponse obsolète (un appel plus récent est parti) : on ignore.
        if (callId !== callIdRef.current) return;

        const data = res.data;
        const taux = tauxRawToPourcentage(data.taux_resolu);

        setResult({
          grille: {
            uuid: data.uuid,
            name: data.name,
            validFrom: data.valid_from,
            validTo: data.valid_to,
          },
          taux,
          modeResolution: data.mode_resolution,
        });
        if (data.mode_resolution === "introuvable" || taux === null) {
          setError(
            "Aucun taux résolu pour cette loi de roulage — saisir le taux manuellement."
          );
        }
      } catch (err) {
        if (callId !== callIdRef.current) return;
        setResult(null);
        setError(
          err.response?.data?.message ||
            "Aucune grille trouvée — saisir le taux manuellement."
        );
      } finally {
        if (callId === callIdRef.current) setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [marque, modele, carburant, dur, km]);

  return {
    grille: result ? result.grille : null,
    taux: result && result.taux !== null ? result.taux : null,
    modeResolution: result ? result.modeResolution : null,
    isLoading,
    error,
  };
};

export default useSimulatedRateVR;
